export type ChangelogEntry = {
  version: string;
  date: string;
  title: string;
  added?: string[];
  improved?: string[];
  fixed?: string[];
};

export const changelogEntries: ChangelogEntry[] = [
  {
    version: 'v1.16.0',
    date: 'August 2026',
    title: 'Clearer advertising disclosure',
    improved: [
      'Rewrote the About page advertising disclosure so it states plainly what is true today: display ads only, no affiliate links, and no commission on any product. If we ever add a paid link, we now commit to labelling it as one and keeping it in the written guides rather than inside a calculator.',
      'Sharpened the promise that actually protects readers: we are not paid to steer you toward a particular bank, broker, loan, or fund, and no commercial arrangement changes the numbers a calculator produces or the order in which results appear.'
    ]
  },
  {
    version: 'v1.15.0',
    date: 'July 2026',
    title: 'Site quality and transparency update',
    added: [
      'New "How Our Calculators Work" methodology page documenting the assumptions, formulas, and limits behind every tool, with named sources including Bengen (1994) and the Trinity study.',
      'New guide: "FIRE Number Worked Example" that follows one household through the full arithmetic, a year-by-year projection, and a withdrawal-rate and return sensitivity table.',
      'New guide: "Choosing a Safe Withdrawal Rate" covering where the 4% rule came from, sequence-of-returns risk with a worked illustration, and why early retirees often use 3.25-3.75%.',
      'Expanded About page describing who builds Fire-Calc, how the calculators are tested, our editorial standards, and our advertising-only, no-affiliate independence.'
    ],
    improved: [
      'Debt payoff engine now rolls each freed-up minimum payment into the next target debt, so avalanche and snowball comparisons reflect how a focused strategy actually accelerates.',
      'Accessibility improvements across content and calculator pages.'
    ],
    fixed: [
      'Debt payoff calculator now warns when an entered payment is too small to outpace interest, meaning the balance would never be paid off.',
      'Removed unused placeholder ad slots from page templates.'
    ]
  },
  {
    version: 'v1.14.0',
    date: 'June 1, 2026',
    title: 'New Equity Calculator',
    added: [
      'New Equity Calculator for modeling your whole equity stack in one place — RSUs, stock options (ISO/NSO), ESPP, restricted stock (RSAs), and SAR/phantom/performance (PSU) grants.',
      'Plots vested value, unvested value, and after-tax-if-liquidated value year by year, with a per-grant breakdown plus CSV, PDF, and shareable-link export.'
    ]
  },
  {
    version: 'v1.13.0',
    date: 'May 3, 2026',
    title: 'First-time homebuyer guide refinements',
    improved: [
      'Expanded the affordability table up to $250,000 of gross annual income for at-a-glance reference.',
      'Added a "reality check" callout distinguishing the 28% lender ceiling from a more conservative ~20%-of-gross personal-finance target.',
      'Clarified that income figures in the affordability table are gross (pre-tax).'
    ],
    fixed: [
      'Corrected approximate home price ranges to reflect actual PITI math at 7% / 30-year fixed / 20% down with realistic property tax and insurance assumptions.',
      'Footnote inside the affordability table now has proper padding instead of butting up against the container edge.'
    ]
  },
  {
    version: 'v1.12.0',
    date: 'April 27, 2026',
    title: 'Job Offer Comparison calculator',
    added: [
      'New Job Offer Comparison calculator with tax-aware equity modeling, RSU appreciation, and a labeled breakeven chart.'
    ],
    improved: [
      'Wide layout for the Job Offer calculator with a "pool-held" column for clearer equity comparison.',
      'Clarified gross vs. net labels in the Advanced FIRE drawdown flow.'
    ],
    fixed: [
      'Advanced drawdown cascade ordering for capped 401(k) and overflow handling.',
      'RSU appreciation calculation in the Job Offer engine, with regression tests added and the offer count capped at 2.'
    ]
  },
  {
    version: 'v1.11.0',
    date: 'April 4, 2026',
    title: 'Expanded calculator articles and scenario refinements',
    improved: [
      'Expanded long-form educational content across calculator pages.',
      'Consolidated scenario library content for better information architecture.'
    ]
  },
  {
    version: 'v1.10.0',
    date: 'March 28, 2026',
    title: 'Drawdown calculation correctness fix',
    fixed: [
      'Withdrawal rate now applies as a gross portfolio percentage rather than a net post-withholding figure, aligning the math with how the rule is conventionally stated.'
    ]
  },
  {
    version: 'v1.9.0',
    date: 'March 19, 2026',
    title: 'Calculator UI refresh',
    improved: [
      'Refreshed calculator UI with updated chart and card components.',
      'Added "Route Next Steps" cards across calculator and content pages to guide readers to related tools.',
      'Polished FIRE calculator copy for readability.'
    ]
  },
  {
    version: 'v1.8.0',
    date: 'February 23, 2026',
    title: 'Scenario Library and Guides hub',
    added: [
      'New Scenario Library pages.',
      'Guides hub page consolidating long-form educational content.',
      'Long-form educational content on the Compound Interest and Coast FIRE calculator pages.'
    ]
  },
  {
    version: 'v1.7.0',
    date: 'February 7, 2026',
    title: 'Advanced withdrawal strategy and navigation improvements',
    added: [
      'New in-app changelog page with route aliases for direct linking and indexing.',
      'Advanced FIRE controls for penalty-free age, 401(k) bracket cap, and cap growth handling.',
      'How this works panels on every calculator page with route-aware explanations.'
    ],
    improved: [
      'Desktop navigation now surfaces all calculators via primary links plus a More Calculators menu.',
      'Top-level resources and guides are available as first-class in-app content routes.',
      'Advanced FIRE exports now include withdrawal target, source mix, and shortfall columns.'
    ],
    fixed: [
      'Advanced withdrawal cascade now preserves withdrawal targets when 401(k) assets remain.',
      'Post-59 drawdown ordering now prioritizes capped 401(k) first, then taxable, then above-cap 401(k), preserving Roth for last.'
    ]
  },
  {
    version: 'v1.6.0',
    date: 'February 2, 2026',
    title: 'Share and export upgrades',
    added: [
      'Shareable URL state for all calculator routes.',
      'CSV and PDF export support across calculator workflows.'
    ],
    improved: [
      'Route alias handling for existing `.html` calculator URLs.',
      'Consistent summary cards and result table structures.'
    ]
  },
  {
    version: 'v1.5.0',
    date: 'January 26, 2025',
    title: 'Calculator suite expansion',
    added: [
      'Additional housing, debt, budgeting, and paycheck calculators.',
      'Expanded educational guides and reference pages.'
    ]
  }
];
